import { jsPDF } from "jspdf";

export type InvoiceLine = {
  reference: string;
  performance_date: string | null;
  address: string | null;
  fee_pence: number; // total incl. fuel
  fuel_charge_pence?: number;
  distance_miles?: number | null;
  paid: boolean;
};

export type InvoiceInput = {
  invoiceNo: string;
  clientName: string;
  contactName?: string | null;
  clientEmail?: string | null;
  lines: InvoiceLine[];
  dueDate?: string | null;
};

const gbp = (p: number) => `£${(p / 100).toFixed(2)}`;
const ukDate = (iso: string) =>
  new Date(iso.length === 10 ? iso + "T12:00:00" : iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

/** Builds a clean A4 invoice PDF from booking data. */
export function buildInvoicePdf(inv: InvoiceInput): Blob {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const L = 20;
  const R = W - 20;
  const amber: [number, number, number] = [196, 149, 106];
  const ink: [number, number, number] = [26, 26, 26];
  const grey: [number, number, number] = [120, 120, 120];

  doc.setFillColor(13, 13, 13);
  doc.rect(0, 0, W, 42, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("times", "normal");
  doc.setFontSize(24);
  doc.text("Olivia Flemming", L, 24);
  doc.setFontSize(9);
  doc.setTextColor(...amber);
  doc.text("SINGER  ·  OLIVIAFLEMMING.CO.UK  ·  HELLO@OLIVIAFLEMMING.CO.UK", L, 32);
  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);
  doc.text("INVOICE", R, 24, { align: "right" });

  let y = 58;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...grey);
  doc.text("BILLED TO", L, y);
  doc.text("INVOICE NO.", 125, y);
  doc.setFontSize(11);
  doc.setTextColor(...ink);
  doc.text(inv.clientName, L, y + 6);
  let by = y + 11;
  doc.setFontSize(9);
  if (inv.contactName) { doc.text(inv.contactName, L, by); by += 5; }
  if (inv.clientEmail) { doc.text(inv.clientEmail, L, by); by += 5; }
  doc.setFontSize(11);
  doc.text(inv.invoiceNo, 125, y + 6);
  doc.setFontSize(8);
  doc.setTextColor(...grey);
  doc.text("DATE ISSUED", 125, y + 14);
  doc.setFontSize(10);
  doc.setTextColor(...ink);
  doc.text(ukDate(new Date().toISOString()), 125, y + 19);
  if (inv.dueDate) {
    doc.setFontSize(8);
    doc.setTextColor(...grey);
    doc.text("PAYMENT DUE", 165, y + 14);
    doc.setFontSize(10);
    doc.setTextColor(...ink);
    doc.text(ukDate(inv.dueDate), 165, y + 19);
  }

  y = Math.max(by, y + 26) + 10;
  doc.setDrawColor(...amber);
  doc.setLineWidth(0.4);
  doc.line(L, y, R, y);
  y += 6;
  doc.setFontSize(8);
  doc.setTextColor(...grey);
  doc.text("DESCRIPTION", L, y);
  doc.text("AMOUNT", R, y, { align: "right" });
  y += 4;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.2);
  doc.line(L, y, R, y);
  y += 8;

  let total = 0;
  let paid = 0;
  for (const l of inv.lines) {
    const fuel = l.fuel_charge_pence ?? 0;
    const perf = l.fee_pence - fuel;
    total += l.fee_pence;
    if (l.paid) paid += l.fee_pence;
    doc.setFontSize(10);
    doc.setTextColor(...ink);
    doc.text(`Live performance · ${!l.performance_date || l.performance_date === "2099-12-31" ? "Date TBC" : ukDate(l.performance_date)}`, L, y);
    doc.text(gbp(perf), R, y, { align: "right" });
    doc.setFontSize(8);
    doc.setTextColor(...grey);
    const sub = [l.reference, l.address].filter(Boolean).join("  ·  ");
    doc.text(doc.splitTextToSize(sub, 120)[0] ?? "", L, y + 4.5);
    y += 10;
    if (fuel > 0) {
      doc.setFontSize(10);
      doc.setTextColor(...ink);
      doc.text(`Travel / fuel${l.distance_miles ? ` (${l.distance_miles} miles)` : ""}`, L + 4, y);
      doc.text(gbp(fuel), R, y, { align: "right" });
      y += 8;
    }
    if (y > 250) { doc.addPage(); y = 25; }
  }

  doc.setDrawColor(220, 220, 220);
  doc.line(L, y, R, y);
  y += 9;
  const sumRow = (label: string, val: string, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 12 : 10);
    doc.setTextColor(...ink);
    doc.text(label, 140, y);
    doc.text(val, R, y, { align: "right" });
    y += 7;
  };
  sumRow("Total", gbp(total));
  if (paid > 0) sumRow("Already paid", gbp(paid));
  doc.setFillColor(...amber);
  doc.rect(135, y - 5, R - 135 + 2, 10, "F");
  doc.setTextColor(13, 13, 13);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(paid >= total ? "PAID IN FULL" : "Balance due", 138, y + 1.5);
  doc.text(gbp(Math.max(0, total - paid)), R, y + 1.5, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...grey);
  doc.text("Thank you for booking. Pay securely online using your payment link.", L, 275);
  doc.text("Questions? Message Olivia on WhatsApp: 07702 696253", L, 280);

  return doc.output("blob");
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
